/**
 * AM-W1-α.2 — non-render-blocking loader for glass-ui's web-font corpus.
 *
 * The publisher (glass-ui) split its 6 payload-bearing `@font-face` faces
 * (Plus Jakarta Sans + Fira Code, base64-inlined woff2, ~102 KB gz) OUT of
 * the critical `@mkbabb/glass-ui/styles` barrel into a separate subpath
 * export `@mkbabb/glass-ui/styles/fonts` (→ `dist/styles/fonts.css`). The
 * `styles` barrel speedtest `@import`s at `styles/style.css` is now
 * font-free — only zero-payload Capsize `local()` fallback faces remain in
 * its `typography.css`. That keeps the render-blocking `index-*.css` ~99 KB
 * gz lighter (the AM-A4 regression, now permanently banked).
 *
 * Re-adding `@import "@mkbabb/glass-ui/styles/fonts"` to `styles/style.css`
 * would re-bundle that ~102 KB back into the render-blocking entry css and
 * recreate the regression. Instead the caller (index.html's bootstrap)
 * resolves the fonts css via a Vite `?url` import — Rollup emits it as a
 * separate content-hashed asset — and hands the URL here to inject as an
 * async `<link>`.
 *
 * The loading idiom mirrors `vite.config.mjs`'s `ag-wperf-l3-css-async`
 * plugin (the canonical W-PERF.L3 async-`<link>` form for the main sheet):
 * `media="print"` so the browser fetches the stylesheet at low priority
 * without blocking first paint, then an `onload` flip to `media="all"`
 * applies it once it arrives. A `<noscript>` blocking fallback covers the
 * JS-disabled case. This is CSP-clean against `public/_headers`:
 * `style-src 'self'` admits the `<link>`, and `font-src 'self' data:`
 * admits the base64 woff2 faces inside fonts.css.
 *
 * `font-display: optional` (display/serif) + `swap` (Fira Code mono) on the
 * faces themselves — set upstream by glass-ui — keep the swap off the
 * first-paint critical path with zero CLS (Capsize-calibrated fallbacks).
 */
export function loadFonts(fontsHref: string): void {
    if (typeof document === "undefined") return;

    const link = document.createElement("link");
    link.rel = "stylesheet";
    // Low-priority, non-blocking fetch; flip to `all` once loaded so the
    // faces apply without ever gating first paint.
    link.media = "print";
    link.href = fontsHref;
    link.addEventListener(
        "load",
        () => {
            link.media = "all";
        },
        { once: true },
    );

    const noscript = document.createElement("noscript");
    const fallback = document.createElement("link");
    fallback.rel = "stylesheet";
    fallback.href = fontsHref;
    noscript.appendChild(fallback);

    document.head.append(link, noscript);
}
